import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { Combobox } from "../ui/Combobox";
import { Select } from "../ui/Select";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useCampers } from "../../hooks/useCampers";
import { useActivities } from "../../hooks/useActivities";
import { useSeason } from "../../context/SeasonContext";
import { completionsApi } from "../../api/completions";
import { honorsApi } from "../../api/honors";

// ─── Schemas ──────────────────────────────────────────────────────────────────

const acSchema = z.object({
  camperId: z.number().min(1, "Select a camper"),
  activityId: z.number().min(1, "Select an activity"),
  achievementCreditId: z.number().min(1, "Select an AC"),
  termId: z.coerce.number().optional(),
  completedDate: z.string().optional(),
  notes: z.string().optional(),
}).refine(
  (data) => !!data.completedDate || !!data.termId,
  { message: "Enter a date or select a term", path: ["completedDate"] }
);

const honorSchema = z.object({
  camperId: z.number().min(1, "Select a camper"),
  activityId: z.number().min(1, "Select an activity"),
  termId: z.coerce.number().optional(),
  earnedDate: z.string().optional(),
  notes: z.string().optional(),
}).refine(
  (data) => !!data.earnedDate || !!data.termId,
  { message: "Enter a date or select a term", path: ["earnedDate"] }
);

const graduationSchema = z.object({
  camperId: z.number().min(1, "Select a camper"),
  activityId: z.number().min(1, "Select an activity"),
  termId: z.coerce.number().optional(),
  graduationDate: z.string().optional(),
  notes: z.string().optional(),
}).refine(
  (data) => !!data.graduationDate || !!data.termId,
  { message: "Enter a date or select a term", path: ["graduationDate"] }
);

type AcFormData = z.infer<typeof acSchema>;
type HonorFormData = z.infer<typeof honorSchema>;
type GraduationFormData = z.infer<typeof graduationSchema>;

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  mode: "ac" | "honor" | "graduation";
  onSuccess: () => void;
  onCancel: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function QuickEntryForm({ mode, onSuccess, onCancel }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [camperQuery, setCamperQuery] = useState("");

  const { selectedTermId: contextTermId, termsForYear: terms } = useSeason();
  const contextTerm = terms.find((t) => t.id === contextTermId) ?? null;
  const contextTermEndDate = contextTerm?.endDate ?? "";

  const { activities, loading: activitiesLoading } = useActivities();
  const { data: campersData, loading: campersLoading } = useCampers({
    search: camperQuery,
    pageSize: 20,
  });

  const camperOptions = (campersData?.data ?? []).map((c) => ({
    value: c.id,
    label: `${c.firstName} ${c.lastName}`,
  }));

  const activityOptions = activities.map((a) => ({
    value: a.id,
    label: a.name,
  }));

  const termOptions = terms.map((t) => ({ value: t.id, label: t.name }));

  // ── AC form ──────────────────────────────────────────────────────────────
  const acForm = useForm<AcFormData>({
    resolver: zodResolver(acSchema),
    defaultValues: {
      camperId: 0,
      activityId: 0,
      achievementCreditId: 0,
      termId: contextTermId ?? undefined,
      completedDate: contextTermEndDate,
      notes: "",
    },
  });

  // ── Honor form ───────────────────────────────────────────────────────────
  const honorForm = useForm<HonorFormData>({
    resolver: zodResolver(honorSchema),
    defaultValues: {
      camperId: 0,
      activityId: 0,
      termId: contextTermId ?? undefined,
      earnedDate: contextTermEndDate,
      notes: "",
    },
  });

  // ── Graduation form ──────────────────────────────────────────────────────
  const graduationForm = useForm<GraduationFormData>({
    resolver: zodResolver(graduationSchema),
    defaultValues: {
      camperId: 0,
      activityId: 0,
      termId: contextTermId ?? undefined,
      graduationDate: contextTermEndDate,
      notes: "",
    },
  });

  if (mode === "ac") {
    const {
      register,
      control,
      handleSubmit,
      watch,
      setValue,
      formState: { errors },
    } = acForm;

    const selectedActivityId = watch("activityId");
    const selectedActivity = activities.find((a) => a.id === selectedActivityId);
    const acOptions = (selectedActivity?.achievementCredits ?? [])
      .filter((ac) => ac.type !== "graduation")
      .map((ac) => ({
        value: ac.id,
        label: `${ac.displayNumber}. ${ac.name}`,
      }));

    const handleTermChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const termId = Number(e.target.value) || undefined;
      setValue("termId", termId);
      const term = termId ? terms.find((t) => t.id === termId) : undefined;
      setValue("completedDate", term?.endDate ?? "");
    };

    const onSubmit = async (data: AcFormData) => {
      setSubmitting(true);
      try {
        const dateToUse = data.completedDate || terms.find((t) => t.id === data.termId)?.endDate || "";
        await completionsApi.create({
          camperId: data.camperId,
          achievementCreditId: data.achievementCreditId,
          status: "completed",
          completedDate: dateToUse,
          notes: data.notes,
        });
        toast.success("AC recorded!");
        onSuccess();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to record AC");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Controller
          control={control}
          name="camperId"
          render={({ field }) => (
            <Combobox
              label="Camper Name"
              options={camperOptions}
              value={field.value || null}
              onChange={(v) => field.onChange(v ?? 0)}
              onQueryChange={setCamperQuery}
              placeholder={campersLoading ? "Loading…" : "Type to search campers…"}
              error={errors.camperId?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="activityId"
          render={({ field }) => (
            <Combobox
              label="Activity Name"
              options={activityOptions}
              value={field.value || null}
              onChange={(v) => {
                field.onChange(v ?? 0);
                setValue("achievementCreditId", 0);
              }}
              placeholder={activitiesLoading ? "Loading…" : "Type to search activities…"}
              error={errors.activityId?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="achievementCreditId"
          render={({ field }) => (
            <Combobox
              label="AC Name"
              options={acOptions}
              value={field.value || null}
              onChange={(v) => field.onChange(v ?? 0)}
              placeholder={
                !selectedActivityId
                  ? "Select an activity first"
                  : acOptions.length === 0
                  ? "No ACs for this activity"
                  : "Type to search ACs…"
              }
              disabled={!selectedActivity || acOptions.length === 0}
              error={errors.achievementCreditId?.message}
            />
          )}
        />

        <Select
          label="Term"
          options={termOptions}
          placeholder={termOptions.length === 0 ? "No terms available" : "— No term —"}
          disabled={termOptions.length === 0}
          {...register("termId")}
          onChange={handleTermChange}
        />

        <Input
          label="Date"
          type="date"
          error={errors.completedDate?.message}
          {...register("completedDate")}
        />

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">
            Notes (optional)
          </label>
          <textarea
            {...register("notes")}
            rows={3}
            placeholder="Any relevant notes…"
            className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Save AC
          </Button>
        </div>
      </form>
    );
  }

  // ── Graduation mode ─────────────────────────────────────────────────────────
  if (mode === "graduation") {
    const {
      register,
      control,
      handleSubmit,
      watch,
      setValue,
      formState: { errors },
    } = graduationForm;

    const selectedActivityId = watch("activityId");
    const selectedActivity = activities.find((a) => a.id === selectedActivityId);

    const handleTermChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const termId = Number(e.target.value) || undefined;
      setValue("termId", termId);
      const term = termId ? terms.find((t) => t.id === termId) : undefined;
      setValue("graduationDate", term?.endDate ?? "");
    };

    const onSubmit = async (data: GraduationFormData) => {
      setSubmitting(true);
      try {
        const graduationAc = selectedActivity?.achievementCredits?.find(
          (ac) => ac.type === "graduation"
        );
        if (!graduationAc) {
          toast.error("No graduation achievement found for this activity");
          return;
        }
        const dateToUse = data.graduationDate || terms.find((t) => t.id === data.termId)?.endDate || "";
        await completionsApi.create({
          camperId: data.camperId,
          achievementCreditId: graduationAc.id,
          status: "completed",
          completedDate: dateToUse,
          notes: data.notes,
        });
        toast.success("Graduation recorded!");
        onSuccess();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to record graduation");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Controller
          control={control}
          name="camperId"
          render={({ field }) => (
            <Combobox
              label="Camper Name"
              options={camperOptions}
              value={field.value || null}
              onChange={(v) => field.onChange(v ?? 0)}
              onQueryChange={setCamperQuery}
              placeholder={campersLoading ? "Loading…" : "Type to search campers…"}
              error={errors.camperId?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="activityId"
          render={({ field }) => (
            <Combobox
              label="Activity Name"
              options={activityOptions}
              value={field.value || null}
              onChange={(v) => field.onChange(v ?? 0)}
              placeholder={activitiesLoading ? "Loading…" : "Type to search activities…"}
              error={errors.activityId?.message}
            />
          )}
        />

        <Select
          label="Term"
          options={termOptions}
          placeholder={termOptions.length === 0 ? "No terms available" : "— No term —"}
          disabled={termOptions.length === 0}
          {...register("termId")}
          onChange={handleTermChange}
        />

        <Input
          label="Graduation Date"
          type="date"
          error={errors.graduationDate?.message}
          {...register("graduationDate")}
        />

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">
            Notes (optional)
          </label>
          <textarea
            {...register("notes")}
            rows={3}
            placeholder="Any relevant notes…"
            className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Record Graduation
          </Button>
        </div>
      </form>
    );
  }

  // ── Honor mode ─────────────────────────────────────────────────────────────
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = honorForm;

  const handleTermChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const termId = Number(e.target.value) || undefined;
    setValue("termId", termId);
    const term = termId ? terms.find((t) => t.id === termId) : undefined;
    setValue("earnedDate", term?.endDate ?? "");
  };

  const onSubmit = async (data: HonorFormData) => {
    setSubmitting(true);
    try {
      const selectedTerm = terms.find((t) => t.id === data.termId);
      const dateToUse = data.earnedDate || selectedTerm?.endDate || "";
      const year = selectedTerm?.year ?? new Date(dateToUse).getFullYear();
      await honorsApi.create({
        camperId: data.camperId,
        activityId: data.activityId,
        level: "honor",
        year,
        earnedDate: dateToUse,
        notes: data.notes,
      });
      toast.success("Honor awarded!");
      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to award honor");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Controller
        control={control}
        name="camperId"
        render={({ field }) => (
          <Combobox
            label="Camper Name"
            options={camperOptions}
            value={field.value || null}
            onChange={(v) => field.onChange(v ?? 0)}
            onQueryChange={setCamperQuery}
            placeholder={campersLoading ? "Loading…" : "Type to search campers…"}
            error={errors.camperId?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="activityId"
        render={({ field }) => (
          <Combobox
            label="Activity Name"
            options={activityOptions}
            value={field.value || null}
            onChange={(v) => field.onChange(v ?? 0)}
            placeholder={activitiesLoading ? "Loading…" : "Type to search activities…"}
            error={errors.activityId?.message}
          />
        )}
      />

      <Select
        label="Term"
        options={termOptions}
        placeholder={termOptions.length === 0 ? "No terms available" : "— No term —"}
        disabled={termOptions.length === 0}
        {...register("termId")}
        onChange={handleTermChange}
      />

      <Input
        label="Date"
        type="date"
        error={errors.earnedDate?.message}
        {...register("earnedDate")}
      />

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          Notes (optional)
        </label>
        <textarea
          {...register("notes")}
          rows={3}
          placeholder="Reason for honor…"
          className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={submitting}>
          Award Honor
        </Button>
      </div>
    </form>
  );
}
